/**
 * Sizes and colours (step 3c of the owner's plan, 2026-09-26; migration 112).
 *
 * A MODEL is a group of ordinary products: every size (and colour) is its own
 * row with its own stock, barcode and price, tied together by `model_id`. So
 * everything that already works on a product — the receipt, a return, the
 * stocktake, the waybill, the reports — works on a size without knowing it.
 * Only three places group them: the till's tile + size picker, the warehouse
 * list, and the dialog that creates a model as a size × colour grid.
 *
 * The rules those three share live here, once.
 */
import { ean13CheckDigit } from './scale-barcode'
import type { Product } from './mock-data'

export const SIZE_PRESETS: { key: string; label: string; sizes: string[] }[] = [
  { key: 'clothes', label: 'ტანსაცმელი', sizes: ['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL'] },
  { key: 'shoes', label: 'ფეხსაცმელი', sizes: ['35', '36', '37', '38', '39', '40', '41', '42', '43', '44', '45', '46'] },
  { key: 'jeans', label: 'ჯინსი (წელი)', sizes: ['26', '27', '28', '29', '30', '31', '32', '33', '34', '36', '38'] },
  { key: 'kids', label: 'საბავშვო (სმ)', sizes: ['86', '92', '98', '104', '110', '116', '122', '128', '134', '140', '146', '152', '158', '164'] },
]

export const COLOR_SUGGESTIONS = [
  'შავი', 'თეთრი', 'ნაცრისფერი', 'ლურჯი', 'მუქი ლურჯი', 'წითელი', 'მწვანე', 'ყვითელი', 'ვარდისფერი', 'ბეჟი', 'ყავისფერი',
]

const clean = (s: string | null | undefined) => (s ?? '').trim().replace(/\s+/g, ' ')

/** „Nike Air · 42 · შავი" — what the receipt, the cart and the waybill say. */
export function variantName(model: string, size?: string | null, color?: string | null): string {
  return [clean(model), clean(size), clean(color)].filter(Boolean).join(' · ')
}

/** „42 · შავი" — the variant inside its model's group. */
export function variantLabel(p: Pick<Product, 'size' | 'color'>): string {
  return [clean(p.size), clean(p.color)].filter(Boolean).join(' · ')
}

/** The database's uniqueness (112): one size+colour per model, case and spaces aside. */
export function variantKey(size?: string | null, color?: string | null): string {
  return `${clean(color).toLowerCase()}|${clean(size).toLowerCase()}`
}

// ─── Size order ──────────────────────────────────────────────────────────────
// XS before S before M; 36 before 42; anything else after, as typed.
const LETTERS = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL', '4XL', '5XL']
const LETTER_ALIAS: Record<string, string> = { '2XL': 'XXL', XXXL: '3XL', XXXXL: '4XL' }

function sizeRank(size: string): [number, number, string] {
  const s = clean(size).toUpperCase()
  const letter = LETTERS.indexOf(LETTER_ALIAS[s] ?? s)
  if (letter >= 0) return [0, letter, s]
  const n = /^(\d+(?:[.,]\d+)?)/.exec(s)
  if (n) return [1, Number(n[1].replace(',', '.')), s]
  return [2, 0, s]
}

export function compareSizes(a: string | null | undefined, b: string | null | undefined): number {
  const [ga, va, sa] = sizeRank(a ?? '')
  const [gb, vb, sb] = sizeRank(b ?? '')
  return ga - gb || va - vb || sa.localeCompare(sb)
}

/** Which of compareSizes' three groups a size is in: letters (XS…5XL), a number (36, 42.5), or anything else. */
export function sizeFamily(size: string | null | undefined): 'letter' | 'number' | 'other' {
  return (['letter', 'number', 'other'] as const)[sizeRank(size ?? '')[0]]
}

// ─── The shop's own barcode ──────────────────────────────────────────────────
// GS1 keeps EAN-13 codes starting with 2 for use inside one shop; the app
// numbers its own from 29: `29` + a 10-digit number + the check digit. The
// scale's labels use 21/22 by default, and a code that IS a product's barcode
// is always read as that product first (see the till's scale path).

/** Same rule as migration 112 — a shop's own code never goes to the shared catalog. */
export const isStoreCode = (code: string | null | undefined) => /^2(\d{12}|\d{7})$/.test((code ?? '').trim())

/**
 * The next `n` in-store codes after the highest the shop already has. Read
 * from the code's middle ten digits, whatever its check digit — the robot
 * shop's first codes were typed by hand.
 */
export function nextStoreCodes(existing: (string | null | undefined)[], n: number): string[] {
  let max = 0
  for (const c of existing) {
    const m = /^29(\d{10})\d$/.exec((c ?? '').trim())
    if (m) max = Math.max(max, Number(m[1]))
  }
  const out: string[] = []
  for (let i = 1; i <= n; i++) {
    const first12 = '29' + String(max + i).padStart(10, '0')
    out.push(first12 + String(ean13CheckDigit(first12)))
  }
  return out
}

// ─── Grouping ────────────────────────────────────────────────────────────────

export type ModelGroup = {
  id: string
  name: string
  /** Sorted: by colour (first seen), then by size. */
  variants: Product[]
  sizes: string[]
  colors: string[]
  stock: number
  minPrice: number
  maxPrice: number
  photo: string | null
  category_id: string
}

function sortVariants(vs: Product[]): Product[] {
  const colorOrder = new Map<string, number>()
  // Colours in the order they were created — the owner typed them that way.
  for (const v of [...vs].sort((a, b) => (a.created_at ?? '').localeCompare(b.created_at ?? ''))) {
    const c = clean(v.color).toLowerCase()
    if (!colorOrder.has(c)) colorOrder.set(c, colorOrder.size)
  }
  return [...vs].sort((a, b) =>
    (colorOrder.get(clean(a.color).toLowerCase())! - colorOrder.get(clean(b.color).toLowerCase())!)
    || compareSizes(a.size, b.size))
}

export function summarizeModel(id: string, variants: Product[]): ModelGroup {
  const vs = sortVariants(variants)
  const sizes: string[] = []
  const colors: string[] = []
  for (const v of vs) {
    const s = clean(v.size)
    const c = clean(v.color)
    if (s && !sizes.some((x) => x.toLowerCase() === s.toLowerCase())) sizes.push(s)
    if (c && !colors.some((x) => x.toLowerCase() === c.toLowerCase())) colors.push(c)
  }
  sizes.sort(compareSizes)
  const prices = vs.map((v) => v.sale_price)
  return {
    id,
    name: clean(vs[0]?.model_name) || vs[0]?.name || '',
    variants: vs,
    sizes,
    colors,
    stock: vs.reduce((s, v) => s + (v.quantity > 0 ? v.quantity : 0), 0),
    minPrice: prices.length ? Math.min(...prices) : 0,
    maxPrice: prices.length ? Math.max(...prices) : 0,
    photo: vs.find((v) => v.photo_url)?.photo_url ?? null,
    category_id: vs[0]?.category_id ?? '',
  }
}

/**
 * The one size of a model that can be sold, when there is only one — the till
 * then puts it in the cart on a tap instead of opening a picker with a single
 * choice (the owner, 2026-09-28: LEVIS 32, AIR MAX 41 and NEW BALANCE 40 each
 * opened a picker to press „32" in). Several sizes in stock: null, pick.
 */
export function onlySellable(model: Pick<ModelGroup, 'variants'>): Product | null {
  const sellable = model.variants.filter((v) => v.quantity > 0)
  return sellable.length === 1 ? sellable[0] : null
}

export type ListEntry =
  | { kind: 'product'; product: Product }
  | { kind: 'model'; model: ModelGroup }

/**
 * The list with every model folded to one entry, at the place of its first
 * size in the given order (newest first in the app). `products` is whatever
 * the screen already filtered — a search for „42" shows each model with its
 * size 42 only.
 */
export function foldModels(products: Product[]): ListEntry[] {
  const byModel = new Map<string, Product[]>()
  for (const p of products) {
    if (!p.model_id) continue
    const list = byModel.get(p.model_id)
    if (list) list.push(p)
    else byModel.set(p.model_id, [p])
  }
  const out: ListEntry[] = []
  const seen = new Set<string>()
  for (const p of products) {
    if (!p.model_id) { out.push({ kind: 'product', product: p }); continue }
    if (seen.has(p.model_id)) continue
    seen.add(p.model_id)
    out.push({ kind: 'model', model: summarizeModel(p.model_id, byModel.get(p.model_id)!) })
  }
  return out
}

/** Every model of the shop, by id — for the dialogs that open one. */
export function modelsById(products: Product[]): Map<string, ModelGroup> {
  const out = new Map<string, ModelGroup>()
  for (const e of foldModels(products)) if (e.kind === 'model') out.set(e.model.id, e.model)
  return out
}

/**
 * The labels to print for a model's sizes: one per unit on the shelf. A size
 * with none gets none; if NOTHING has stock yet (labels printed before the
 * goods arrive), one of each.
 */
export function labelCounts(list: Pick<Product, 'id' | 'quantity'>[]): Record<string, number> {
  const stocked = list.filter((p) => p.quantity > 0)
  return Object.fromEntries((stocked.length > 0 ? stocked : list).map((p) => [p.id, Math.max(1, Math.round(p.quantity))]))
}

// ─── A photo per colour ──────────────────────────────────────────────────────
// A colour's photo lives on its sizes' `photo_url`, like any product's — the
// till, the list and the label read it with no model logic. Only the model's
// form speaks of „the model's photo" and „a colour's own".

const colorKey = (c: string | null | undefined) => clean(c).toLowerCase()

/** The photo the till shows for one colour: its own, else the model's. */
export function colorPhoto(model: Pick<ModelGroup, 'variants' | 'photo'>, color: string): string | null {
  const k = colorKey(color)
  return model.variants.find((v) => colorKey(v.color) === k && v.photo_url)?.photo_url ?? model.photo
}

/**
 * The photos as the model's form shows them: `base` — the one most colours
 * share (the model's own) — and each colour whose photo is another. When no
 * photo is shared by two colours there is no base: changing the model's photo
 * must not silently replace a photo the owner gave one colour. A model with
 * fewer than two colours has one photo, as before.
 */
export function modelPhotos(model: Pick<ModelGroup, 'variants' | 'colors' | 'photo'>): { base: string | null; byColor: Record<string, string> } {
  if (model.colors.length < 2) return { base: model.photo, byColor: {} }
  const photoOf = new Map<string, string | null>()
  for (const v of model.variants) {
    const k = colorKey(v.color)
    if (!photoOf.get(k)) photoOf.set(k, v.photo_url ?? null)
  }
  const count = new Map<string, number>()
  for (const ph of photoOf.values()) if (ph) count.set(ph, (count.get(ph) ?? 0) + 1)
  let base: string | null = null
  let best = 1
  for (const [ph, n] of count) if (n > best) { base = ph; best = n }
  const byColor: Record<string, string> = {}
  for (const [k, ph] of photoOf) if (ph && ph !== base) byColor[k] = ph
  return { base, byColor }
}

/** The photo a size of this colour should carry under the form's choice. */
export function photoFor(color: string | null | undefined, base: string | null, byColor: Record<string, string>): string | null {
  return byColor[colorKey(color)] ?? base ?? null
}

/** Size id → its new photo, for the sizes whose photo the form changes. */
export function photoWrites(
  variants: Pick<Product, 'id' | 'color' | 'photo_url'>[],
  base: string | null,
  byColor: Record<string, string>,
): Record<string, string | null> {
  const out: Record<string, string | null> = {}
  for (const v of variants) {
    const want = photoFor(v.color, base, byColor)
    if ((v.photo_url ?? null) !== want) out[v.id] = want
  }
  return out
}

// ─── What a new model starts with (1.33) ─────────────────────────────────────

/**
 * The size preset a NEW model of this category opens on: the one that shares
 * most sizes with the category's models — and when the category has none yet
 * (a new brand under „ბოტასები"), with its root's and the root's children's.
 * Null when nothing is known: the form keeps the preset it shows. It was
 * always „ტანსაცმელი", so every shoe model began with a wrong row of sizes.
 */
export function presetForCategory(
  products: Pick<Product, 'model_id' | 'size' | 'category_id'>[],
  categories: { id: string; parent_id: string | null }[],
  categoryId: string,
): string | null {
  const cat = categories.find((c) => c.id === categoryId)
  if (!cat) return null
  const sizesIn = (ids: Set<string>) => {
    const out: string[] = []
    for (const p of products) {
      const s = clean(p.size)
      if (p.model_id && s && ids.has(p.category_id) && !out.some((x) => x.toLowerCase() === s.toLowerCase())) out.push(s)
    }
    return out
  }
  let sizes = sizesIn(new Set([cat.id]))
  if (sizes.length === 0) {
    const rootId = cat.parent_id ?? cat.id
    sizes = sizesIn(new Set([rootId, ...categories.filter((c) => c.parent_id === rootId).map((c) => c.id)]))
  }
  let best: string | null = null
  let most = 0
  for (const preset of SIZE_PRESETS) {
    const n = sizes.filter((s) => preset.sizes.some((x) => x.toLowerCase() === s.toLowerCase())).length
    if (n > most) { best = preset.key; most = n }
  }
  return best
}

/**
 * The shop's own colour words, most used first — counted once per MODEL (a
 * model's eight sizes in black are one black), ties to the newest. The fixed
 * list did not have „სტაფილოს ფერი" or „რძისფერი"; X-TRAND typed them by hand.
 */
export function shopColors(products: Pick<Product, 'model_id' | 'color' | 'created_at'>[], limit = 6): string[] {
  const seen = new Map<string, { word: string; models: Set<string>; last: string }>()
  for (const p of products) {
    const word = clean(p.color)
    if (!p.model_id || !word) continue
    const k = word.toLowerCase()
    const e = seen.get(k) ?? { word, models: new Set<string>(), last: '' }
    e.models.add(p.model_id)
    if ((p.created_at ?? '') > e.last) e.last = p.created_at ?? ''
    seen.set(k, e)
  }
  return [...seen.values()]
    .sort((a, b) => b.models.size - a.models.size || b.last.localeCompare(a.last))
    .slice(0, limit)
    .map((e) => e.word)
}

// ─── Sales by model ──────────────────────────────────────────────────────────

export type SoldRow = { product_id: string | null; name: string; qty: number; revenue: number; cost: number }
export type SoldEntry = {
  name: string
  qty: number
  revenue: number
  cost: number
  /** Set on a model's row; its sizes' units under it, net of returns. */
  model_id: string | null
  sizes: { label: string; qty: number }[]
}

/**
 * Sales per product folded into their models: „LEVIS 501" once, with how many
 * of each size went. The sale lines stay the size's own (a receipt, a return
 * and the cost work per size); only the report groups them. A size deleted
 * since sold under its own name — its sale lines lost the link (SET NULL) —
 * and stays a row of its own. Sorted by revenue.
 */
export function foldSoldByModel(
  rows: SoldRow[],
  products: Pick<Product, 'id' | 'name' | 'model_id' | 'model_name' | 'size' | 'color'>[],
): SoldEntry[] {
  const byId = new Map(products.map((p) => [p.id, p]))
  const out = new Map<string, SoldEntry & { parts: { size: string | null | undefined; color: string | null | undefined; label: string; qty: number }[] }>()
  rows.forEach((r, i) => {
    const p = r.product_id ? byId.get(r.product_id) : undefined
    if (!p?.model_id) {
      out.set(`p:${i}`, { name: r.name, qty: r.qty, revenue: r.revenue, cost: r.cost, model_id: null, sizes: [], parts: [] })
      return
    }
    const key = `m:${p.model_id}`
    const e = out.get(key) ?? { name: clean(p.model_name) || r.name, qty: 0, revenue: 0, cost: 0, model_id: p.model_id, sizes: [], parts: [] }
    e.qty += r.qty
    e.revenue += r.revenue
    e.cost += r.cost
    e.parts.push({ size: p.size, color: p.color, label: variantLabel(p) || r.name, qty: r.qty })
    out.set(key, e)
  })
  return [...out.values()]
    .map(({ parts, ...e }) => ({
      ...e,
      sizes: parts
        .filter((x) => x.qty !== 0)
        .sort((a, b) => colorKey(a.color).localeCompare(colorKey(b.color)) || compareSizes(a.size, b.size))
        .map(({ label, qty }) => ({ label, qty })),
    }))
    .sort((a, b) => b.revenue - a.revenue)
}

/** „32 × 3 · 34 × 2" — a model's sizes as the report's line under its name. */
export function soldSizesLine(sizes: SoldEntry['sizes']): string {
  return sizes.map((s) => `${s.label} × ${Number.isInteger(s.qty) ? s.qty : s.qty.toFixed(2)}`).join(' · ')
}

/** „₾45" or „₾45–55". */
export function priceRange(m: Pick<ModelGroup, 'minPrice' | 'maxPrice'>): string {
  return m.minPrice === m.maxPrice
    ? `₾${m.minPrice.toFixed(2)}`
    : `₾${m.minPrice.toFixed(2)}–${m.maxPrice.toFixed(2)}`
}
